import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const UserDashboard = () => {
  const { currentUser } = useAuth();

  // State for each widget
  const [profileViews, setProfileViews] = useState([]);
  const [viewedProfiles, setViewedProfiles] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [favoriteMe, setFavoriteMe] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);

  // Loading states
  const [loadingViews, setLoadingViews] = useState(true);
  const [loadingViewedProfiles, setLoadingViewedProfiles] = useState(true);
  const [loadingFavorites, setLoadingFavorites] = useState(true);
  const [loadingFavoriteMe, setLoadingFavoriteMe] = useState(true);
  const [loadingBlocked, setLoadingBlocked] = useState(true);

  // Fetch profile views (who viewed me)
  useEffect(() => {
    const fetchProfileViews = async () => {
      try {
        setLoadingViews(true);
        const response = await api.get('/view-me');
        setProfileViews(response.data.users || []);
      } catch (err) {
        console.error('Error fetching profile views:', err);
      } finally {
        setLoadingViews(false);
      }
    };

    fetchProfileViews();
  }, []);

  // Fetch profiles I viewed
  useEffect(() => {
    const fetchViewedProfiles = async () => {
      try {
        setLoadingViewedProfiles(true);
        const response = await api.get('/my-views');
        setViewedProfiles(response.data.users || []);
      } catch (err) {
        console.error('Error fetching viewed profiles:', err);
      } finally {
        setLoadingViewedProfiles(false);
      }
    };

    fetchViewedProfiles();
  }, []);

  // Fetch favorites
  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        setLoadingFavorites(true);
        const response = await api.get('/my-favorite');
        setFavorites(response.data.users || []);
      } catch (err) {
        console.error('Error fetching favorites:', err);
      } finally {
        setLoadingFavorites(false);
      }
    };

    fetchFavorites();
  }, []);

  // Fetch who favorited me
  useEffect(() => {
    const fetchFavoriteMe = async () => {
      try {
        setLoadingFavoriteMe(true);
        const response = await api.get('/favorite-me');
        setFavoriteMe(response.data.users || []);
      } catch (err) {
        console.error('Error fetching who favorited me:', err);
      } finally {
        setLoadingFavoriteMe(false);
      }
    };

    fetchFavoriteMe();
  }, []);

  // Fetch blocked users
  useEffect(() => {
    const fetchBlockedUsers = async () => {
      try {
        setLoadingBlocked(true);
        const response = await api.get('/blocked');
        setBlockedUsers(response.data.users || []);
      } catch (err) {
        console.error('Error fetching blocked users:', err);
      } finally {
        setLoadingBlocked(false);
      }
    };

    fetchBlockedUsers();
  }, []);

  // Helper function to render user avatars
  const renderUserAvatar = (user) => {
    return user.avatars?.small ? (
      <img
        src={user.avatars.small}
        alt={user.username}
        className="rounded-circle"
        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
      />
    ) : (
      <div
        className="rounded-circle bg-light d-flex align-items-center justify-content-center"
        style={{ width: '40px', height: '40px' }}
      >
        <i className="bi bi-person-fill text-secondary"></i>
      </div>
    );
  };

  // Widget component for each section
  const Widget = ({ title, icon, users, loading, emptyMessage, viewAllLink, viewAllText }) => {
    return (
      <Card className="border-0 shadow-sm h-100">
        <Card.Body className="p-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <i className={`bi ${icon} fs-4 text-primary me-2`}></i>
              <h5 className="fw-bold mb-0">{title}</h5>
            </div>
            {users.length > 0 && (
              <Badge bg="primary" pill>
                {users.length}
              </Badge>
            )}
          </div>

          {loading ? (
            <div className="text-center py-3">
              <Spinner animation="border" variant="primary" size="sm" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-3">
              <p className="text-muted mb-0">{emptyMessage}</p>
            </div>
          ) : (
            <div className="user-list">
              {users.slice(0, 3).map(user => (
                <div key={user.username} className="d-flex align-items-center mb-2 p-2 rounded hover-bg-light">
                  <div className="me-2">
                    {renderUserAvatar(user)}
                  </div>
                  <div>
                    <Link to={`/users/${user.username}`} className="text-decoration-none">
                      <p className="mb-0 fw-bold">{user.username}</p>
                    </Link>
                    <small className="text-muted">
                      {user.age && `${user.age} • `}
                      {user.city || ''}
                    </small>
                  </div>
                </div>
              ))}

              {users.length > 3 && (
                <div className="text-center mt-3">
                  <Button as={Link} to={viewAllLink} variant="outline-primary" size="sm">
                    {viewAllText} ({users.length})
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  return (
    <div className="user-dashboard py-4">
      <h4 className="fw-bold mb-4">Your Dashboard</h4>

      <Row>
        <Col lg={4} md={6} className="mb-4">
          <Widget
            title="Profile Views"
            icon="bi-eye-fill"
            users={profileViews}
            loading={loadingViews}
            emptyMessage="No one has viewed your profile yet"
            viewAllLink="/view-me"
            viewAllText="View all profile views"
          />
        </Col>

        <Col lg={4} md={6} className="mb-4">
          <Widget
            title="Profiles You Viewed"
            icon="bi-eye"
            users={viewedProfiles}
            loading={loadingViewedProfiles}
            emptyMessage="You haven't viewed any profiles yet"
            viewAllLink="/my-views"
            viewAllText="View all profiles you viewed"
          />
        </Col>

        <Col lg={4} md={6} className="mb-4">
          <Widget
            title="Your Favorites"
            icon="bi-heart-fill"
            users={favorites}
            loading={loadingFavorites}
            emptyMessage="You haven't favorited anyone yet"
            viewAllLink="/favorited-users"
            viewAllText="View all favorites"
          />
        </Col>

        <Col lg={4} md={6} className="mb-4">
          <Widget
            title="Who Favorited You"
            icon="bi-heart"
            users={favoriteMe}
            loading={loadingFavoriteMe}
            emptyMessage="No one has favorited you yet"
            viewAllLink="/favorite-me"
            viewAllText="View all who favorited you"
          />
        </Col>

        <Col lg={4} md={6} className="mb-4">
          <Widget
            title="Blocked Users"
            icon="bi-shield-fill"
            users={blockedUsers}
            loading={loadingBlocked}
            emptyMessage="You haven't blocked anyone"
            viewAllLink="/blocked-users"
            viewAllText="View all blocked users"
          />
        </Col>

        <Col lg={4} md={6} className="mb-4">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center mb-3">
                <i className="bi bi-gear-fill fs-4 text-primary me-2"></i>
                <h5 className="fw-bold mb-0">Privacy & Security</h5>
              </div>

              <p className="text-muted mb-3">Manage your privacy settings and account security</p>

              <div className="d-grid">
                <Button as={Link} to="/settings" variant="outline-primary">
                  <i className="bi bi-gear me-2"></i>
                  Settings
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UserDashboard;
